/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { canManageCQs } from '../permissions'
import { callClaude } from '../claude'
import * as XLSX from 'xlsx'

const STATUS_CONFIG = {
  awaiting_response: { label: 'Awaiting Response', colour: 'C9A84C', bg: 'FFF8E7' },
  responded:         { label: 'Responded',         colour: '2D6A4F', bg: 'EEF7F2' },
  closed:            { label: 'Closed',             colour: '718096', bg: 'F7FAFC' },
}

function ClarificationQuestions({ opportunity, profile, onClose }) {
  const [cqs, setCqs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingCq, setEditingCq] = useState(null)
  const [expandedCq, setExpandedCq] = useState(null)
  const [error, setError] = useState(null)
  const [importing, setImporting] = useState(false)
  const [analysingId, setAnalysingId] = useState(null)
  const [importPreview, setImportPreview] = useState(null)

  const [form, setForm] = useState({
    question: '',
    related_question: '',
    asked_by_us: false,
    customer_response: '',
    response_date: '',
    status: 'awaiting_response',
    notes: ''
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCqs() }, [])

  console.log("CQ ai_enabled:", profile?.organisations?.ai_enabled)
  const loadCqs = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('clarification_questions')
      .select('*, ai_impact, response_updated_at')
      .eq('opportunity_id', opportunity.id)
      .order('cq_number')
    setCqs(data || [])
    setLoading(false)
  }

  const handleAnalyseImpact = async (cq) => {
    setAnalysingId(cq.id)
    try {
      const system = `You are an expert bid manager analysing the impact of a clarification question response on a Government and Defence bid. Be concise, precise, and commercially focused. Respond in JSON only.`

      const prompt = `Analyse the impact of this clarification question response on the bid.

Opportunity: ${opportunity.title}
Buyer: ${opportunity.buyer}

Clarification Question: ${cq.question}
${cq.related_question ? 'Related ITT Question: ' + cq.related_question : ''}
Customer Response: ${cq.customer_response || 'No response yet'}

Respond with a JSON object containing:
{
  "impact_level": "High" | "Medium" | "Low" | "None",
  "impact_summary": "One sentence summary of the impact",
  "bid_implications": "2-3 sentences on what this means for the bid",
  "recommended_actions": ["action 1", "action 2"]
}`

      const result = await callClaude({ system, prompt, max_tokens: 500 })
      const clean = result.replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(clean)

      const { error: aiError } = await supabase
        .from('clarification_questions')
        .update({ ai_impact: parsed })
        .eq('id', cq.id)
      console.log('AI save result:', aiError, 'parsed:', parsed)

      setCqs(prev => prev.map(q => q.id === cq.id ? { ...q, ai_impact: parsed } : q))

    } catch (err) {
      console.error('AI analysis error:', err)
    }
    setAnalysingId(null)
  }

  const resetForm = () => {
    setForm({ question: '', asked_by_us: false, customer_response: '', response_date: '', status: 'awaiting_response', notes: '' })
    setShowAdd(false)
    setEditingCq(null)
    setError(null)
  }

  const handleSave = async () => {
    if (!form.question.trim()) return
    setError(null)

    const payload = {
      question: form.question.trim(),
      related_question: form.related_question || null,
      asked_by_us: form.asked_by_us,
      customer_response: form.customer_response || null,
      response_date: form.response_date || null,
      status: form.status,
      notes: form.notes || null
    }

    if (editingCq) {
      const { error } = await supabase
        .from('clarification_questions')
        .update(payload)
        .eq('id', editingCq.id)
      if (error) { setError(error.message); return }
      setCqs(prev => prev.map(q => q.id === editingCq.id ? { ...q, ...payload } : q))
    } else {
      const { data, error } = await supabase
        .from('clarification_questions')
        .insert([{ ...payload, opportunity_id: opportunity.id, organisation_id: profile.organisation_id, created_by: profile.id }])
        .select()
        .single()
      if (error) { setError(error.message); return }
      setCqs(prev => [...prev, data])
    }
    resetForm()
  }

  const handleEdit = (cq) => {
    setForm({
      question: cq.question || '',
      related_question: cq.related_question || '',
      asked_by_us: cq.asked_by_us || false,
      customer_response: cq.customer_response || '',
      response_date: cq.response_date ? new Date(cq.response_date).toISOString().split('T')[0] : '',
      status: cq.status || 'awaiting_response',
      notes: cq.notes || ''
    })
    setEditingCq(cq)
    setShowAdd(true)
    setExpandedCq(null)
  }

  const handleDelete = async (id) => {
    await supabase.from('clarification_questions').delete().eq('id', id)
    setCqs(prev => prev.filter(q => q.id !== id))
  }

  const handleStatusChange = async (id, newStatus) => {
    await supabase.from('clarification_questions').update({ status: newStatus }).eq('id', id)
    setCqs(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q))
  }

  // ── Excel Export ──────────────────────────────────────────────────────────
  const handleExport = () => {
    const rows = cqs.map(cq => ({
      'CQ Number': cq.cq_number,
      'Question': cq.question,
      'Asked By Us': cq.asked_by_us ? 'Yes' : 'No',
      'Status': STATUS_CONFIG[cq.status]?.label || cq.status,
      'Customer Response': cq.customer_response || '',
      'Response Date': cq.response_date ? new Date(cq.response_date).toLocaleDateString('en-GB') : '',
      'Notes': cq.notes || ''
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    ws['!cols'] = [
      { wch: 10 }, { wch: 60 }, { wch: 12 }, { wch: 20 },
      { wch: 60 }, { wch: 15 }, { wch: 40 }
    ]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Clarification Questions')
    XLSX.writeFile(wb, `CQ Register — ${opportunity.title}.xlsx`)
  }

  // ── Excel Import ──────────────────────────────────────────────────────────
  const handleImportFile = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImporting(true)
    setError(null)

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws)

        // Try to map columns flexibly
        const preview = rows.map((row, i) => {
          const keys = Object.keys(row)
          const findCol = (...names) => {
            for (const name of names) {
              const key = keys.find(k => k.toLowerCase().includes(name.toLowerCase()))
              if (key) return row[key]
            }
            return ''
          }

          return {
            cq_number: findCol('CQ Number', 'number', 'cq') || (i + 1),
            question: findCol('Question', 'query', 'clarification') || '',
            asked_by_us: String(findCol('Asked By Us', 'asked by', 'our question')).toLowerCase() === 'yes',
            customer_response: findCol('Customer Response', 'response', 'answer', 'reply') || '',
            response_date: findCol('Response Date', 'date', 'answered') || '',
            status: findCol('Status') || 'awaiting_response',
            notes: findCol('Notes', 'comments') || ''
          }
        }).filter(row => row.question)

        setImportPreview(preview)
        setImporting(false)
      } catch (err) {
        setError('Could not read the Excel file. Please check the format and try again.')
        setImporting(false)
      }
    }
    reader.readAsBinaryString(file)
  }

  const handleImportConfirm = async () => {
    if (!importPreview) return
    setError(null)

    for (const row of importPreview) {
      // Try to find existing CQ by number and update, or insert new
      const existing = cqs.find(q => q.cq_number === parseInt(row.cq_number))

      if (existing) {
        await supabase
          .from('clarification_questions')
          .update({
            customer_response: row.customer_response || null,
            response_date: row.response_date ? new Date(row.response_date) : null,
            status: row.customer_response ? 'responded' : existing.status,
            notes: row.notes || existing.notes,
            response_updated_at: row.customer_response && row.customer_response !== existing.customer_response ? new Date().toISOString() : existing.response_updated_at
          })
          .eq('id', existing.id)
      } else {
        const { error: _ignored } = await supabase
          .from('clarification_questions')
          .insert([{
            opportunity_id: opportunity.id,
            organisation_id: profile.organisation_id,
            question: row.question,
            asked_by_us: row.asked_by_us,
            customer_response: row.customer_response || null,
            response_date: row.response_date ? new Date(row.response_date) : null,
            status: row.customer_response ? 'responded' : 'awaiting_response',
            notes: row.notes || null,
            created_by: profile.id
          }])
      }
    }

    setImportPreview(null)
    await loadCqs()
    setError(null)
  }

  const awaiting = cqs.filter(q => q.status === 'awaiting_response').length
  const responded = cqs.filter(q => q.status === 'responded').length

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>

      {/* Header */}
      <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>
              Clarification Questions
            </div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 600, color: 'var(--white)' }}>
              {opportunity.title}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>{opportunity.buyer}</div>
          </div>
          {cqs.length > 0 && (
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', fontWeight: 700, color: 'var(--white)', lineHeight: 1 }}>{cqs.length}</div>
                <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>Total CQs</div>
              </div>
              {awaiting > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', fontWeight: 700, color: '#C9A84C', lineHeight: 1 }}>{awaiting}</div>
                  <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>Awaiting</div>
                </div>
              )}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.5rem', fontWeight: 700, color: 'var(--green-light)', lineHeight: 1 }}>{responded}</div>
                <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>Responded</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Import preview */}
      {importPreview && (
        <div style={{ background: 'var(--green-pale)', border: '1px solid var(--green-light)', padding: '20px', marginBottom: '24px', borderLeft: '4px solid var(--green)' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '8px' }}>
            Import Preview — {importPreview.length} rows found
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--slate)', margin: '0 0 12px' }}>
            Existing CQs will be updated with customer responses. New CQs will be added.
          </p>
          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '16px' }}>
            {importPreview.slice(0, 5).map((row, i) => (
              <div key={i} style={{ fontSize: '0.8125rem', padding: '6px 0', borderBottom: '1px solid var(--green-light)', color: 'var(--charcoal)' }}>
                <strong>CQ {row.cq_number}:</strong> {row.question?.substring(0, 80)}{row.question?.length > 80 ? '...' : ''}
                {row.customer_response && <span style={{ color: 'var(--green)', marginLeft: '8px' }}>✓ Has response</span>}
              </div>
            ))}
            {importPreview.length > 5 && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--slate)', padding: '6px 0' }}>...and {importPreview.length - 5} more</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn--primary" onClick={handleImportConfirm}>Confirm Import</button>
            <button className="btn btn--secondary" onClick={() => setImportPreview(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Add / Edit form */}
      {showAdd && (
        <div style={{ background: 'var(--white)', border: '1px solid var(--green-light)', padding: '24px', marginBottom: '24px', borderLeft: '4px solid var(--green)' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '16px' }}>
            {editingCq ? `Edit CQ ${editingCq.cq_number}` : 'Add Clarification Question'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Question *</label>
              <textarea
                value={form.question}
                onChange={e => setForm({ ...form, question: e.target.value })}
                placeholder="Enter the clarification question in full"
                rows={3}
                autoFocus
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Related ITT Question (optional)</label>
              <input
                type="text"
                value={form.related_question}
                onChange={e => setForm({ ...form, related_question: e.target.value })}
                placeholder="e.g. Section 4, Question 3.2"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={{ width: '100%' }}>
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '24px' }}>
              <input
                type="checkbox"
                id="asked-by-us"
                checked={form.asked_by_us}
                onChange={e => setForm({ ...form, asked_by_us: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <label htmlFor="asked-by-us" style={{ cursor: 'pointer', marginBottom: 0 }}>
                Asked by us
              </label>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Customer Response</label>
              <textarea
                value={form.customer_response}
                onChange={e => setForm({ ...form, customer_response: e.target.value })}
                placeholder="Paste the buyer's response here when received"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Response Date</label>
              <input
                type="date"
                value={form.response_date}
                onChange={e => setForm({ ...form, response_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Notes (internal)</label>
              <textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes on this CQ and its impact on our bid"
                rows={2}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button className="btn btn--primary" onClick={handleSave} disabled={!form.question.trim()}>
              {editingCq ? 'Save Changes' : 'Add CQ'}
            </button>
            <button className="btn btn--secondary" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-dark)' }}>
          {cqs.length} Question{cqs.length !== 1 ? 's' : ''}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!showAdd && (
            <button className="btn btn--primary" style={{ padding: '6px 16px', fontSize: '0.8125rem' }} onClick={() => setShowAdd(true)}>
              + Add CQ
            </button>
          )}
          {cqs.length > 0 && (
            <button
              className="btn btn--secondary"
              style={{ padding: '6px 16px', fontSize: '0.8125rem' }}
              onClick={handleExport}
            >
              ↓ Export Excel
            </button>
          )}
          <label
            style={{
              display: 'inline-block', padding: '6px 16px',
              background: 'none', border: '1px solid var(--green)',
              color: 'var(--green)', fontSize: '0.8125rem', fontWeight: 600,
              cursor: 'pointer', letterSpacing: '0.04em'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = 'var(--white)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--green)' }}
          >
            {importing ? 'Reading...' : '↑ Import Excel'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFile}
              style={{ display: 'none' }}
              disabled={importing}
            />
          </label>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--slate)' }}>Loading clarification questions...</p>}

      {!loading && cqs.length === 0 && (
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
            No clarification questions yet. Click <strong>+ Add CQ</strong> to add your first question, or import from Excel.
          </p>
        </div>
      )}

      {cqs.map(cq => {
        const statusConfig = STATUS_CONFIG[cq.status]
        const isExpanded = expandedCq === cq.id

        return (
          <div
            key={cq.id}
            style={{
              background: 'var(--white)',
              border: '1px solid var(--green-light)',
              borderLeft: `4px solid #${statusConfig.colour}`,
              marginBottom: '8px', transition: 'box-shadow 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(45,106,79,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            <div
              style={{ padding: '14px 20px', cursor: 'pointer' }}
              onClick={() => setExpandedCq(isExpanded ? null : cq.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--green-dark)', background: 'var(--green-pale)', padding: '2px 8px', flexShrink: 0 }}>
                      CQ {cq.cq_number}
                    </span>
                    <span style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: `#${statusConfig.colour}`, background: `#${statusConfig.bg}`, padding: '2px 8px' }}>
                      {statusConfig.label}
                    </span>
                    {cq.asked_by_us && (
                      <span style={{ fontSize: '0.625rem', fontWeight: 600, color: 'var(--green)', background: 'var(--green-pale)', padding: '2px 8px' }}>
                        Asked by us
                      </span>
                    )}
                    {cq.response_updated_at && (!cq.ai_impact) && cq.customer_response && (
                      <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--white)', background: '#E67E22', padding: '2px 8px', letterSpacing: '0.06em', animation: 'pulse 2s infinite' }}>
                        NEW RESPONSE
                      </span>
                    )}
                    {cq.response_date && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>
                        Response: {new Date(cq.response_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {cq.related_question && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--slate)', fontStyle: 'italic' }}>
                        Re: {cq.related_question}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.9375rem', color: 'var(--charcoal)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                    {cq.question}
                  </div>
                  {!isExpanded && cq.customer_response && (
                    <div style={{ fontSize: '0.8125rem', color: 'var(--slate)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ↳ {cq.customer_response}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <select
                    value={cq.status}
                    onChange={e => { e.stopPropagation(); handleStatusChange(cq.id, e.target.value) }}
                    onClick={e => e.stopPropagation()}
                    style={{ padding: '4px 8px', border: `1px solid #${statusConfig.colour}`, fontSize: '0.75rem', fontWeight: 600, color: `#${statusConfig.colour}`, background: `#${statusConfig.bg}`, borderRadius: 0, outline: 'none', cursor: 'pointer' }}
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>
            </div>

            {isExpanded && (
              <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--green-light)' }}>
                {cq.related_question && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Related ITT Question</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5 }}>{cq.related_question}</div>
                  </div>
                )}
                {cq.customer_response && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Customer Response</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--charcoal)', lineHeight: 1.5, background: 'var(--green-pale)', padding: '10px 14px' }}>{cq.customer_response}</div>
                  </div>
                )}
                {cq.ai_impact && (
                  <div style={{ marginTop: '12px', background: 'var(--green-dark)', padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)' }}>AI Impact Analysis</div>
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                        padding: '2px 10px',
                        background: cq.ai_impact.impact_level === 'High' ? 'var(--red)' : cq.ai_impact.impact_level === 'Medium' ? '#E67E22' : 'var(--green)',
                        color: 'var(--white)'
                      }}>
                        {cq.ai_impact.impact_level} Impact
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--white)', marginBottom: '10px', lineHeight: 1.4 }}>
                      {cq.ai_impact.impact_summary}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--green-light)', lineHeight: 1.5, marginBottom: '10px' }}>
                      {cq.ai_impact.bid_implications}
                    </div>
                    {cq.ai_impact.recommended_actions && cq.ai_impact.recommended_actions.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>Recommended Actions</div>
                        {cq.ai_impact.recommended_actions.map((action, i) => (
                          <div key={i} style={{ fontSize: '0.8125rem', color: 'var(--white)', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '8px' }}>
                            <span style={{ color: 'var(--green-light)', flexShrink: 0 }}>{i + 1}.</span>
                            {action}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--green-light)' }}>
                  {cq.customer_response && profile?.organisations?.ai_enabled && (
                    <button
                      onClick={() => handleAnalyseImpact(cq)}
                      disabled={analysingId === cq.id}
                      style={{
                        background: analysingId === cq.id ? 'var(--green-pale)' : 'none',
                        border: '1px solid var(--green-dark)',
                        color: 'var(--green-dark)', padding: '4px 14px',
                        fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                        opacity: analysingId === cq.id ? 0.7 : 1
                      }}
                    >
                      {analysingId === cq.id ? '⏳ Analysing...' : '✦ Analyse Impact'}
                    </button>
                  )}
                  <button onClick={() => handleEdit(cq)} style={{ background: 'none', border: '1px solid var(--green)', color: 'var(--green)', padding: '4px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                    Edit
                  </button>
                  <button
                    onClick={() => canManageCQs(profile) && handleDelete(cq.id)}
                  disabled={!canManageCQs(profile)}
                  style={{ opacity: canManageCQs(profile) ? 1 : 0.3, cursor: canManageCQs(profile) ? 'pointer' : 'default', background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '4px 10px', fontSize: '0.75rem', marginLeft: 'auto' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}

      <div className="actions" style={{ marginTop: '16px' }}>
        <button className="btn btn--secondary" onClick={onClose}>← Back to Pipeline</button>
      </div>
    </div>
  )
}

export default ClarificationQuestions